import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Apps from '@mui/icons-material/Apps';
import Code from '@mui/icons-material/Code';
import JoinLeft from '@mui/icons-material/JoinLeft';
import PersonSearch from '@mui/icons-material/PersonSearch';
import PieChart from '@mui/icons-material/PieChart';
import Security from '@mui/icons-material/Security';
import Share from '@mui/icons-material/Share';
import Terminal from '@mui/icons-material/Terminal';
import ChevronRight from '@mui/icons-material/ChevronRight';
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import { Button } from '~/components/Button';
import { Link } from '~/components/Link';
import type { ProductGroup } from './types';
import { FindInPageOutlined, SupportAgentOutlined } from '@mui/icons-material';

export const customCrawlsItem = {
  href: '/deepweb',
  title: 'Custom crawls',
  description:
    "Need code we don't track? We'll 3+ billion pages and find any pattern you give us.",
};

export const productMenuGroups: ProductGroup[] = [
  {
    title: 'Find websites',
    items: [
      {
        href: '/reports/new',
        icon: () => <FindInPageOutlined />,
        title: 'Code Search',
        description: 'List websites by technology or code',
      },
      {
        href: '/technologies',
        icon: () => <Apps />,
        title: 'Browse technologies',
        description: 'By software and industry',
      },
      {
        href: '/domains',
        icon: () => <PersonSearch />,
        title: 'Domain search',
        description: "A domain's tech stack and company data",
      },
    ],
  },
  {
    title: 'Analyze',
    items: [
      {
        href: '/compare',
        icon: () => <PieChart />,
        title: 'Compare technologies',
        description: 'Market share head to head',
      },
      {
        href: '/overlap',
        icon: () => <JoinLeft />,
        title: 'Overlap analysis',
        description: 'Sites that run both',
      },
      {
        href: '/deepweb',
        icon: () => <SupportAgentOutlined />,
        title: 'Deepweb crawls',
        description: 'Scan any past month and year',
      },
      {
        href: '/security-research',
        icon: () => <Security />,
        title: 'Security research',
        description: 'Find exposed keys and code',
      },
    ],
  },
  {
    title: 'Integrate',
    items: [
      {
        href: '/api',
        icon: () => <Code />,
        title: 'API access',
        description: 'Automate and integrate data',
      },
      {
        href: '/mcp',
        icon: () => <Terminal />,
        title: 'MCP server',
        description: 'Claude, Cursor, ChatGPT, Windsurf',
      },
      {
        href: '/integrations',
        icon: () => <Share />,
        title: 'Apps',
        description: 'Connect the apps you use',
      },
    ],
  },
];

export const ProductMenu = () => {
  const [menuElement, setMenuElement] = React.useState<null | HTMLElement>(
    null
  );

  const onMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuElement(event.currentTarget);
  };

  const onMenuClose = () => {
    setMenuElement(null);
  };

  return (
    <>
      <Button
        onClick={onMenuOpen}
        endIcon={<ArrowDropDown />}
        variant="contained"
        disableElevation
        aria-haspopup="true"
        aria-expanded={Boolean(menuElement)}
        sx={{
          my: 2,
          mx: 1,
          backgroundColor: '#033d6c',
        }}>
        Products
      </Button>
      <Popover
        anchorEl={menuElement}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={Boolean(menuElement)}
        onClose={onMenuClose}>
        <Box sx={{ width: 850, maxWidth: '100%' }}>
          <Stack
            direction="row"
            spacing={2}
            divider={<Divider orientation="vertical" flexItem />}
            sx={{ px: 2, py: 2 }}>
            {productMenuGroups.map((group) => {
              return (
                <Box key={group.title} sx={{ flex: 1 }}>
                  <Typography
                    variant="overline"
                    color="primary"
                    sx={{ px: 1, fontWeight: 500 }}>
                    {group.title}
                  </Typography>
                  <List disablePadding>
                    {group.items.map((item) => {
                      return (
                        <ListItemButton
                          key={item.href}
                          component={Link}
                          href={item.href}
                          underline="none"
                          color="inherit"
                          onClick={onMenuClose}
                          sx={{ px: 1, alignItems: 'flex-start' }}>
                          <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                            <item.icon />
                          </ListItemIcon>
                          <ListItemText
                            primary={item.title}
                            secondary={item.description}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Box>
              );
            })}
          </Stack>
          <Divider />
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'grey.50',
              px: 3,
              py: 2,
            }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {customCrawlsItem.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {customCrawlsItem.description}
              </Typography>
            </Box>
            <Button
              href={customCrawlsItem.href}
              onClick={onMenuClose}
              endIcon={<ChevronRight />}
              sx={{ flexShrink: 0 }}>
              Start a crawl
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};
